import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Route as RouteIcon, TrendingUp, Trash2 } from 'lucide-react';
import { DataTable, Column } from '@/components/common/DataTable';
import { Pagination } from '@/components/common/Pagination';
import { SearchBar } from '@/components/common/SearchBar';
import { Button } from '@/components/common/Button';
import { Badge } from '@/components/common/Badge';
import { Card } from '@/components/common/Card';
import { PageHeader } from '@/components/common/PageHeader';
import { useToast } from '@/components/common/Toast';
import apiClient from '@/services/api/apiClient';
import * as accountsService from '@/services/api/accountsService';
import * as contactsService from '@/services/api/contactsService';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuthStore } from '@/hooks/useAuthStore';

interface Journey {
  id: string;
  contactId: string;
  contactName: string;
  accountId: string;
  accountName: string;
  currentStage: number;
  completedStages: number[];
  status: 'IN_PROGRESS' | 'COMPLETED';
  journeyType: string;
  startedAt: string;
}

const JOURNEY_STAGES = [
  { id: 1, title: 'Account & Contact Context', phase: 'Discovery' },
  { id: 2, title: 'Maturity Mirror Exposure', phase: 'Discovery' },
  { id: 3, title: 'Role-Based Pain Framing', phase: 'Discovery' },
  { id: 4, title: 'Maturity Gap Awareness', phase: 'Discovery' },
  { id: 5, title: 'MES Reality Alignment', phase: 'Discovery' },
  { id: 6, title: 'Alignment Call #1', phase: 'Advisory' },
  { id: 7, title: 'Architecture Thinking', phase: 'Advisory' },
  { id: 8, title: 'Multi-System Coexistence', phase: 'Advisory' },
  { id: 9, title: 'Data Readiness', phase: 'Education' },
  { id: 10, title: 'Automation Timing', phase: 'Education' },
  { id: 11, title: 'Decision Intelligence', phase: 'Education' },
  { id: 12, title: 'Confirmation Call #2', phase: 'Validation' },
  { id: 13, title: 'Future-State Snapshot', phase: 'Validation' },
  { id: 14, title: 'MQL Validation & Handoff', phase: 'Validation' },
];

const PARTNER_JOURNEY_STAGES = [
  { id: 1, title: 'Partner & Contact Context', phase: 'Discovery' },
  { id: 2, title: 'Portfolio Gap Exposure', phase: 'Discovery' },
  { id: 3, title: 'Market Demand Framing', phase: 'Discovery' },
  { id: 4, title: 'Margin & Revenue Opportunity', phase: 'Discovery' },
  { id: 5, title: 'Product Fit & Differentiation', phase: 'Discovery' },
  { id: 6, title: 'Partnership Alignment Call #1', phase: 'Advisory' },
  { id: 7, title: 'Enablement & Delivery Model', phase: 'Advisory' },
  { id: 8, title: 'Commercial & Channel Terms', phase: 'Advisory' },
  { id: 9, title: 'Lead & Pipeline Readiness', phase: 'Education' },
  { id: 10, title: 'Go-To-Market Timing', phase: 'Education' },
  { id: 11, title: 'Joint Business Planning', phase: 'Education' },
  { id: 12, title: 'Partnership Confirmation Call #2', phase: 'Validation' },
  { id: 13, title: 'Onboarding & Ramp Snapshot', phase: 'Validation' },
  { id: 14, title: 'PQL Validation & Handoff', phase: 'Validation' },
];

export default function ABMJourneysListPage() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { user } = useAuthStore();
  const isAdmin = ['ADMIN', 'ADMINISTRATOR'].includes((user?.role || '').toUpperCase());
  const { canCreate } = usePermissions('abm_journey');

  const [journeys, setJourneys] = useState<Journey[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const deleteJourney = async (row: Journey) => {
    if (!window.confirm(`Delete the ABM journey for ${row.contactName}? This cannot be undone.`)) return;
    try {
      await apiClient.delete(`/api/abm-journeys/${row.id}`);
      setJourneys(prev => prev.filter(j => j.id !== row.id));
      addToast({ type: 'success', title: 'Journey deleted', description: `${row.contactName}'s journey was removed.` });
    } catch (e: any) {
      addToast({ type: 'error', title: 'Delete failed', description: e?.response?.data?.message || e?.response?.data?.error || e.message });
    }
  };

  useEffect(() => {
    loadJourneys();
  }, []);

  const loadJourneys = async () => {
    try {
      setLoading(true);

      // Fetch journeys, contacts, and accounts in parallel
      const [journeysRes, contactsRes, accountsRes] = await Promise.all([
        apiClient.get('/api/abm-journeys?limit=1000'),
        contactsService.getContacts({ limit: 1000 }),
        accountsService.getAccounts({ limit: 1000 }),
      ]);

      const rawJourneys: any[] = journeysRes.data?.data || [];
      const contacts: any[] = contactsRes.data || [];
      const accounts: any[] = accountsRes.data || [];

      const enriched: Journey[] = rawJourneys.map((j: any) => {
        // Prefer contact/account embedded in the journey response (resolves even
        // when there are more contacts than the capped global fetch returns).
        const contact = j.contact || contacts.find((c: any) => c.id === j.contactId);
        const account = j.account || accounts.find((a: any) => a.id === j.accountId);

        const contactName = contact
          ? contact.name || `${contact.firstName || ''} ${contact.lastName || ''}`.trim()
          : 'Unknown Contact';

        const completedStages: number[] = Array.isArray(j.completedStages) ? j.completedStages : [];

        return {
          id: j.id,
          contactId: j.contactId,
          contactName,
          accountId: j.accountId,
          accountName: account?.name || 'Unknown Account',
          currentStage: j.currentStage || 1,
          completedStages,
          status: completedStages.length === 14 ? 'COMPLETED' : 'IN_PROGRESS',
          journeyType: (j.journeyType || account?.accountType || 'BUYER').toString().toUpperCase(),
          startedAt: j.createdAt || new Date().toISOString(),
        };
      });

      console.log(`📊 Loaded ${enriched.length} ABM journeys from API`);
      setJourneys(enriched);
    } catch (error) {
      console.error('❌ Error loading ABM journeys:', error);
      // Fallback: try localStorage for backward compatibility
      const loadedJourneys: Journey[] = [];
      const contacts = JSON.parse(localStorage.getItem('contacts') || '[]');
      const accounts = JSON.parse(localStorage.getItem('accounts') || '[]');

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('abm-journey-')) {
          try {
            const data = JSON.parse(localStorage.getItem(key) || '{}');
            const contactIdFromKey = key.replace('abm-journey-', '');
            const contactId = data.contactId || contactIdFromKey;
            const contact = contacts.find((c: any) => String(c.id) === String(contactId));
            const contactName = contact
              ? contact.name || `${contact.firstName || ''} ${contact.lastName || ''}`.trim()
              : 'Unknown Contact';
            const account = accounts.find((a: any) => String(a.id) === String(data.accountId));
            const completedStages = Array.isArray(data.completedStages) ? data.completedStages : [];

            loadedJourneys.push({
              id: key,
              contactId,
              contactName,
              accountId: data.accountId || '',
              accountName: account?.name || 'Unknown Account',
              currentStage: data.currentStage || 1,
              completedStages,
              status: completedStages.length === 14 ? 'COMPLETED' : 'IN_PROGRESS',
              journeyType: (data.journeyType || account?.accountType || 'BUYER').toString().toUpperCase(),
              startedAt: data.stageData?.[1]?.completedAt || new Date().toISOString(),
            });
          } catch (e) {
            console.error('Error parsing localStorage journey:', e);
          }
        }
      }
      setJourneys(loadedJourneys);
    } finally {
      setLoading(false);
    }
  };

  // Filter journeys based on search
  const filteredJourneys = journeys.filter(journey => {
    const query = searchQuery.toLowerCase();
    return (
      journey.contactName.toLowerCase().includes(query) ||
      journey.accountName.toLowerCase().includes(query) ||
      journey.currentStage.toString().includes(query)
    );
  });

  // Pagination
  const totalPages = Math.ceil(filteredJourneys.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedJourneys = filteredJourneys.slice(startIndex, startIndex + pageSize);

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const getStageInfo = (stageNumber: number, journeyType?: string) => {
    const stages = (journeyType || '').toUpperCase() === 'PARTNER' ? PARTNER_JOURNEY_STAGES : JOURNEY_STAGES;
    return stages[stageNumber - 1];
  };

  const columns: Column<Journey>[] = [
    {
      key: 'contactName',
      header: 'Contact',
      accessor: 'contactName',
      sortable: true,
      width: '20%',
      render: (value) => <div className="font-medium">{value}</div>,
    },
    {
      key: 'accountName',
      header: 'Account',
      accessor: 'accountName',
      sortable: true,
      width: '20%',
    },
    {
      key: 'currentStage',
      header: 'Current Stage',
      accessor: 'currentStage',
      sortable: true,
      width: '25%',
      render: (value, row) => {
        const stage = getStageInfo(value as number, row.journeyType);
        const partner = (row.journeyType || '').toUpperCase() === 'PARTNER';
        return (
          <div className="space-y-1">
            <div className="font-medium text-sm">
              Stage {value}: {stage?.title}
            </div>
            <div className="flex items-center gap-1">
              <Badge variant="tier2" className="text-xs">
                {stage?.phase}
              </Badge>
              <Badge variant={partner ? 'mqlReady' : 'tier1'} className="text-xs">
                {partner ? 'Partner' : 'Buyer'}
              </Badge>
            </div>
          </div>
        );
      },
    },
    {
      key: 'progress',
      header: 'Progress',
      accessor: 'completedStages',
      width: '15%',
      render: (value, row) => {
        const completed = (value as number[])?.length || 0;
        const percentage = Math.round((completed / 14) * 100);
        return (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Badge variant={completed === 14 ? 'mqlReady' : 'active'}>
                {completed}/14
              </Badge>
              <span className="text-xs text-muted-foreground">{percentage}%</span>
            </div>
            <div className="w-full bg-muted rounded-full h-1.5">
              <div
                className="bg-primary h-1.5 rounded-full transition-all"
                style={{ width: `${percentage}%` }}
              />
            </div>
          </div>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      accessor: 'status',
      sortable: true,
      width: '12%',
      render: (value) => (
        <Badge variant={value === 'COMPLETED' ? 'mqlReady' : 'active'}>
          {value === 'COMPLETED' ? 'Completed' : 'In Progress'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      accessor: 'id',
      width: '8%',
      render: (_, row) => (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/abm-journeys/${row.contactId}`);
            }}
          >
            <Eye className="h-4 w-4" />
          </Button>
          {isAdmin && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                deleteJourney(row);
              }}
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title="ABM Journeys"
        description="Track contact progression through the 14-step ABM journey"
        actions={
          canCreate ? (
            <Button onClick={() => navigate('/abm-journeys/new')}>
              <RouteIcon className="h-4 w-4 mr-2" />
              Start New Journey
            </Button>
          ) : undefined
        }
      />

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
            <RouteIcon className="h-4 w-4" />
            Total Journeys
          </div>
          <div className="text-2xl font-bold">{journeys.length}</div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
            <TrendingUp className="h-4 w-4" />
            In Progress
          </div>
          <div className="text-2xl font-bold">
            {journeys.filter(j => j.status === 'IN_PROGRESS').length}
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
            <TrendingUp className="h-4 w-4" />
            Completed
          </div>
          <div className="text-2xl font-bold">
            {journeys.filter(j => j.status === 'COMPLETED').length}
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
            <TrendingUp className="h-4 w-4" />
            Avg Progress
          </div>
          <div className="text-2xl font-bold">
            {journeys.length > 0
              ? Math.round(
                  (journeys.reduce((sum, j) => sum + j.completedStages.length, 0) / journeys.length / 14) * 100
                )
              : 0}
            %
          </div>
        </Card>
      </div>

      {/* Search Bar */}
      <SearchBar
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Search by contact, account, or stage..."
      />

      {/* Loading state */}
      {loading && (
        <Card className="p-8 text-center text-muted-foreground">
          Loading ABM journeys...
        </Card>
      )}

      {/* Data Table */}
      {!loading && paginatedJourneys.length > 0 ? (
        <>
          <DataTable
            data={paginatedJourneys}
            columns={columns}
            onRowClick={(journey) => navigate(`/abm-journeys/${journey.contactId}`)}
          />

          {filteredJourneys.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={filteredJourneys.length}
              onPageChange={setCurrentPage}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={[5, 10, 20, 50]}
            />
          )}
        </>
      ) : !loading ? (
        <Card className="p-12 text-center">
          <RouteIcon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No ABM Journeys Found</h3>
          <p className="text-muted-foreground mb-4">
            {searchQuery ? 'No journeys match your search.' : 'Start your first ABM journey to see it here.'}
          </p>
          {!searchQuery && canCreate && (
            <Button onClick={() => navigate('/abm-journeys/new')}>
              <RouteIcon className="h-4 w-4 mr-2" />
              Start New Journey
            </Button>
          )}
        </Card>
      ) : null}
    </div>
  );
}
